import React from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";

/**
 * ReportSummaryTable - Displays totals and averages summary for reports
 * Similar to TotalsTable in InvoiceLayout
 */
const ReportSummaryTable = ({ summary, reportConfig }) => {
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "0";
    return value.toLocaleString();
  };

  // Extract totals and averages from summary
  // Summary can be in two formats:
  // 1. { totals: [...], averages: [...] } - already extracted
  // 2. { totalSum: 100, totalAvg: 50, ... } - needs extraction
  let totals = summary.totals || [];
  let averages = summary.averages || [];

  // If summary doesn't have totals/averages arrays, extract from Sum/Avg fields
  if (totals.length === 0 && averages.length === 0) {
    Object.entries(summary).forEach(([key, value]) => {
      if (key.endsWith("Sum") && typeof value === "number") {
        const fieldName = key.replace("Sum", "");
        const label = fieldName
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
        const isCurrency = /total|amount|pay|charge|price|cost/i.test(fieldName.toLowerCase());
        
        totals.push({
          label,
          value: isCurrency ? formatCurrency(value) : formatNumber(value),
          isCurrency,
        });
      } else if (key.endsWith("Avg") && typeof value === "number") {
        const fieldName = key.replace("Avg", "");
        const label = fieldName
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
        const isCurrency = /total|amount|pay|charge|price|cost/i.test(fieldName.toLowerCase());
        
        averages.push({
          label,
          value: isCurrency ? formatCurrency(value) : typeof value === "number" ? value.toFixed(2) : value,
          isCurrency,
        });
      }
    });
  }

  // Build table data
  const tableData = [];

  // Add totals section
  if (totals.length > 0) {
    tableData.push({ type: "section", label: "Totals" });
    totals.forEach((item) => {
      tableData.push({
        type: "data",
        label: item.label,
        value: item.isCurrency ? formatCurrency(item.value) : formatNumber(item.value),
      });
    });
  }

  // Add averages section
  if (averages.length > 0) {
    tableData.push({ type: "section", label: "Averages" });
    averages.forEach((item) => {
      tableData.push({
        type: "data",
        label: item.label,
        value: item.isCurrency
          ? formatCurrency(item.value)
          : typeof item.value === "number"
          ? item.value.toFixed(2)
          : item.value,
      });
    });
  }

  const columns = React.useMemo(
    () => [
      {
        accessorKey: "label",
        header: "Metric",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: isSection ? "10px" : "9px",
              }}
            >
              {row.original.label}
            </span>
          );
        },
      },
      {
        accessorKey: "value",
        header: "Value",
        cell: ({ row }) => {
          const isSection = row.original.type === "section";
          return (
            <span
              style={{
                fontWeight: isSection ? "bold" : "normal",
                fontSize: isSection ? "10px" : "9px",
                textAlign: "right",
              }}
            >
              {row.original.value || "-"}
            </span>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const thClasses = classNames(
    "border-b-2 py-2 text-xs text-left px-1 font-bold"
  );
  const tdClasses = classNames("py-1 text-sm text-left px-1");

  if (tableData.length === 0) {
    return null;
  }

  return (
    <table id="totals">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id} className={thClasses}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => {
          const isSection = row.original.type === "section";
          return (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <td
                    key={cell.id}
                    className={tdClasses}
                    style={{
                      fontWeight: isSection ? "bold" : "normal",
                      backgroundColor: isSection ? "#f3f4f6" : "white",
                    }}
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ReportSummaryTable;

